import { useState } from 'react';
import { Close, ExpandMore, MenuBook, ShoppingCart } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

import MealLineMeta from '@/components/Parent/MealLineMeta';
import MealServingIcon from '@/components/Parent/MealServingIcon';
import { nfRp } from '@/constants/nf';
import {
	EMPTY_CART_ITEMS,
	cartLineDiscountAmount,
	cartLineNetTotal,
	formatLineDiscountNote,
	linesForChildAndDate,
	mealDateIso,
	mealLineNoteText,
	unpaidTotalForLines,
} from '@/helper/cartFamily';
import { paidDishLinesFromServingTimes } from '@/helper/dashboardStatus';
import useParentDashboardChild from '@/hooks/useParentDashboardChild';

function childShortName(fullName) {
	const nameParts = (fullName || 'Child').trim().split(/\s+/);
	return nameParts.length > 1 && nameParts[0] === nameParts[0].toUpperCase()
		? nameParts[1]
		: nameParts[0];
}

export default function MenuChildOrdersAccordion() {
	const [paidOpen, setPaidOpen] = useState(false);
	const [cartOpen, setCartOpen] = useState(false);
	const selectedCustomer = useSelector((state) => state.auth.selectedCustomer);
	const orderDate = useSelector((state) => state.cart.orderDate);
	const cartItems = useSelector((state) => state.cart.items) ?? EMPTY_CART_ITEMS;
	const dateIso = mealDateIso(orderDate);

	const { childRow } = useParentDashboardChild(orderDate);
	const childId = selectedCustomer?.id;
	const fullName = selectedCustomer?.member?.name || 'Child';
	const childName = childShortName(fullName);
	const dateLabel = dayjs(dateIso || orderDate).format('dddd, D MMMM YYYY');

	const unpaidLines = childId != null ? linesForChildAndDate(cartItems, childId, dateIso) : [];
	const paidLines = paidDishLinesFromServingTimes(childRow?.serving_times);
	const cartQty = unpaidLines.reduce((n, l) => n + (Number(l.quantity) || 0), 0);
	const unpaidTotal = unpaidTotalForLines(unpaidLines);

	if (!cartQty && !paidLines.length) return null;

	return (
		<section className='order-history-context' aria-label='Orders for selected child and date'>
			{paidLines.length > 0 && (
				<div className='context-disclosure'>
					<button
						type='button'
						className='quick-trigger'
						aria-expanded={paidOpen}
						onClick={() => setPaidOpen((v) => !v)}
					>
						<MenuBook fontSize='small' aria-hidden='true' />
						<span>View {childName}&apos;s existing orders</span>
						<ExpandMore
							className={paidOpen ? 'rotate-180' : undefined}
							fontSize='small'
							aria-hidden='true'
						/>
					</button>
					{paidOpen && (
						<div className='quick-panel'>
							<div className='quick-panel-head'>
								<strong>
									{fullName} · Confirmed orders
								</strong>
								<button
									type='button'
									className='quick-panel-close'
									aria-label='Close existing orders'
									onClick={() => setPaidOpen(false)}
								>
									<Close fontSize='small' />
								</button>
							</div>
							<p className='quick-panel-date'>{dateLabel}</p>
							<ul className='quick-panel-list'>
								{paidLines.map((line) => {
									const discNote = formatLineDiscountNote(
										line.discountAmount,
										line.discountName
									);
									return (
										<li key={line.key} className='meal'>
											<MealServingIcon name={line.servingTimeName} />
											<div className='quick-panel-body'>
												<strong>{line.servingTimeName}</strong>
												<p>
													{line.quantity} x {line.name}
												</p>
												<MealLineMeta
													as='small'
													addOns={line.addOns}
													modifiers={line.modifiers}
													note={line.note}
													discountNote={discNote}
												/>
											</div>
											<span className='price'>{nfRp(line.lineTotal)}</span>
										</li>
									);
								})}
							</ul>
							<p className='quick-panel-note'>
								Paid orders above. Items in your cart are not confirmed.
							</p>
						</div>
					)}
				</div>
			)}
			{cartQty > 0 && (
				<div className='context-disclosure'>
					<button
						type='button'
						className='quick-trigger cart-quick-trigger'
						aria-expanded={cartOpen}
						onClick={() => setCartOpen((v) => !v)}
					>
						<ShoppingCart fontSize='small' aria-hidden='true' />
						<span>
							View {childName}&apos;s meals in cart ({cartQty})
						</span>
						<ExpandMore
							className={cartOpen ? 'rotate-180' : undefined}
							fontSize='small'
							aria-hidden='true'
						/>
					</button>
					{cartOpen && (
						<div className='quick-panel quick-panel-cart'>
							<div className='quick-panel-head'>
								<strong>
									{fullName} · Meals in cart
								</strong>
								<button
									type='button'
									className='quick-panel-close'
									aria-label='Close meals in cart'
									onClick={() => setCartOpen(false)}
								>
									<Close fontSize='small' />
								</button>
							</div>
							<p className='quick-panel-date'>{dateLabel}</p>
							<span className='badge draft'>Saved · Not paid</span>
							<ul className='quick-panel-list'>
								{unpaidLines.map((line) => {
									const servingName = line.serving_time?.name || 'Meal';
									const discNote = formatLineDiscountNote(
										cartLineDiscountAmount(line),
										line.discount?.name
									);
									return (
										<li key={line.uid} className='meal'>
											<MealServingIcon name={servingName} />
											<div className='quick-panel-body'>
												<strong>{servingName}</strong>
												<p>
													{line.quantity} x {line.name || line.product_name || 'Item'}
												</p>
												<MealLineMeta
													as='small'
													addOns={line.add_ons}
													modifiers={line.modifiers}
													note={mealLineNoteText(line)}
													discountNote={discNote}
												/>
											</div>
											<span className='price'>{nfRp(cartLineNetTotal(line))}</span>
										</li>
									);
								})}
							</ul>
							<div className='quick-panel-total row'>
								<strong>Unpaid for this date</strong>
								<strong>{nfRp(unpaidTotal)}</strong>
							</div>
							<p className='quick-panel-note'>
								These meals are saved for this child and date. They are confirmed only after
								payment.
							</p>
						</div>
					)}
				</div>
			)}
		</section>
	);
}
